/**
 * TypeScript mirror of docs/timeline-format.md (Fabled Edit Format v2).
 * All times are seconds (float), colors are CSS strings, coordinates normalized 0..1.
 */

export interface EditSettings {
  width: number;
  height: number;
  fps: number;
  sampleRate: number;
  channels: number;
  background: string;
}

export interface PandoraVideoSource {
  kind: 'pandora-video';
  archive: string;
  item: string;
  part: number; // 1-based
  duration: number; // duration of this part (s)
  title: string; // denormalized, display only
  posterFrame: number; // seconds, display only
  url: string | null; // resolved at load time, never persisted
}

export interface ImageSource {
  kind: 'image';
  url: string;
}

export type Source = PandoraVideoSource | ImageSource;

export type TransitionType =
  | 'crossfade'
  | 'fadeblack'
  | 'fadewhite'
  | 'wipeleft'
  | 'wiperight'
  | 'wipeup'
  | 'wipedown'
  | 'slideleft'
  | 'slideright';

export const TRANSITION_TYPES: TransitionType[] = [
  'crossfade',
  'fadeblack',
  'fadewhite',
  'wipeleft',
  'wiperight',
  'wipeup',
  'wipedown',
  'slideleft',
  'slideright',
];

export interface Transition {
  type: TransitionType;
  duration: number;
}

export type Easing = 'linear' | 'easeIn' | 'easeOut' | 'hold';

/**
 * One keyframe of an animated parameter. `t` axis: SOURCE time for media
 * clips (same axis as clip.in/out, before speed — keyframes stay glued to
 * source frames across split/trim/speed changes); seconds from clip.start
 * for overlay clips (which have no source axis).
 */
export interface Keyframe {
  t: number;
  value: number;
  easing: Easing; // interpolation of the segment leaving this keyframe
}

/** A numeric parameter: a static scalar, or keyframed over the clip. */
export type Param = number | { keyframes: Keyframe[] }; // >= 1 keyframe, sorted by t

type EffectOf<P> =
  | { type: 'brightness'; amount: P } // -1..1, 0 = identity
  | { type: 'contrast'; amount: P } // 0..2, 1 = identity
  | { type: 'saturation'; amount: P } // 0..2, 1 = identity
  | { type: 'hue'; degrees: P } // -180..180
  | { type: 'grayscale'; amount: P } // 0..1
  | { type: 'sepia'; amount: P } // 0..1
  | { type: 'invert'; amount: P } // 0..1
  | { type: 'blur'; radius: P }; // px 0..50

/**
 * `id` is stable across reorders and never shared between clips — keyframe
 * param paths ('effects.<id>.amount') and collaborative merges key on it.
 * Optional in persisted v2 documents (generated at load when absent).
 */
export type Effect = EffectOf<Param> & { id: string };
/** Effect with every param sampled to a plain number (render-time shape). */
export type ResolvedEffect = EffectOf<number>;

export type EffectType = Effect['type'];

interface TransformOf<P> {
  scale: P;
  x: P; // offset of clip center from frame center, normalized
  y: P;
  rotate: P; // degrees
  opacity: P;
}

export type Transform = TransformOf<Param>;
/** Transform sampled to plain numbers (render-time shape). */
export type ResolvedTransform = TransformOf<number>;

export interface VideoClip {
  id: string;
  source: string;
  in: number; // source in-point (s, before speed)
  out: number; // source out-point (s)
  start: number; // timeline position (s)
  speed: number; // 0.25..4
  volume: number; // 0..2
  muted: boolean;
  fadeIn: number;
  fadeOut: number;
  effects: Effect[];
  transform: Transform;
  transitionIn: Transition | null;
}

export interface TextStyle {
  fontFamily: 'sans' | 'serif' | 'mono';
  fontSize: number; // fraction of frame HEIGHT
  color: string;
  bold: boolean;
  italic: boolean;
  align: 'left' | 'center' | 'right';
  background: string; // "" for none
  outline: string; // "" for none
  outlineWidth: number; // fraction of frame height
}

export interface TextClip {
  id: string;
  kind: 'text';
  text: string;
  start: number;
  duration: number;
  style: TextStyle;
  position: { x: Param; y: Param }; // anchor (text box center), normalized
  fadeIn: number;
  fadeOut: number;
}

export interface ImageClip {
  id: string;
  kind: 'image';
  source: string;
  start: number;
  duration: number;
  transform: Transform;
  fadeIn: number;
  fadeOut: number;
}

export type OverlayClip = TextClip | ImageClip;

export interface AudioClip {
  id: string;
  source: string;
  in: number; // source in-point (s, before speed)
  out: number; // source out-point (s)
  start: number;
  /**
   * 0.25..4, same axis as VideoClip.speed; timeline duration = (out - in) / speed.
   * Optional so documents written before detach-audio stay valid — absent = 1.
   * Ops always write it; readers must use `?? 1`.
   */
  speed?: number;
  gain: number;
  fadeIn: number;
  fadeOut: number;
}

/** Canvas composite ops a video track may blend onto the tracks below with. */
export type TrackBlend = 'multiply' | 'screen' | 'overlay' | 'darken' | 'lighten';

export const TRACK_BLENDS: TrackBlend[] = [
  'multiply',
  'screen',
  'overlay',
  'darken',
  'lighten',
];

export interface VideoTrack {
  id: string;
  kind: 'video';
  label: string;
  muted: boolean;
  /** Audio solo: when any track is soloed, only soloed tracks are audible. */
  solo?: boolean;
  /** Composite op for the whole track (default normal / source-over). */
  blend?: TrackBlend;
  /** Track-wide opacity 0..1 multiplied into every clip (default 1). */
  opacity?: number;
  clips: VideoClip[];
}

export interface OverlayTrack {
  id: string;
  kind: 'overlay';
  label: string;
  muted: boolean;
  solo?: boolean;
  clips: OverlayClip[];
}

export interface AudioTrack {
  id: string;
  kind: 'audio';
  label: string;
  muted: boolean;
  solo?: boolean;
  gain: number;
  clips: AudioClip[];
}

export type Track = VideoTrack | OverlayTrack | AudioTrack;

export type AnyClip = VideoClip | OverlayClip | AudioClip;

export interface Edit {
  type: 'fabled-edit';
  version: 2;
  settings: EditSettings;
  sources: Record<string, Source>;
  tracks: Track[]; // render order: first = bottom layer
}
